import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { AuthResponse } from '@cardhub/protocol';
import { api, ApiError } from '../api/client';
import { GoogleSignIn } from '../components/GoogleSignIn';
import { GuestButton } from '../components/GuestButton';
import { useT } from '../i18n';
import { errorMessage } from '../lib/hooks';
import { deviceId } from '../lib/device';
import { useSession } from '../store/session';

export function Login() {
  const t = useT();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const next = search.get('next') ?? '/';
  const applyAuth = useSession((s) => s.applyAuth);
  const [mode, setMode] = useState<'password' | 'phone'>('password');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('+977');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [needMfa, setNeedMfa] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const done = (res: AuthResponse) => {
    applyAuth(res);
    nav(next, { replace: true });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'password') done(await api.post<AuthResponse>('/api/v1/auth/login', { identifier, password, deviceId: deviceId(), ...(needMfa ? { mfaCode } : {}) }));
      else if (!codeSent) {
        await api.post('/api/v1/auth/otp/request', { phone });
        setCodeSent(true);
      } else done(await api.post<AuthResponse>('/api/v1/auth/otp/verify', { phone, code, deviceId: deviceId() }));
    } catch (err) {
      if (err instanceof ApiError && err.code === 'MFA_REQUIRED') {
        setNeedMfa(true);
        setError(null);
        return;
      }
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="onboard">
      <h1>{t('auth.login')}</h1>
      <form className="col" onSubmit={(e) => void submit(e)}>
        {mode === 'password' ? (
          <>
            <div className="field">
              <label htmlFor="id">{t('auth.identifier')}</label>
              <input id="id" className="input" autoComplete="username" value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoFocus />
            </div>
            <div className="field">
              <label htmlFor="pw">{t('auth.password')}</label>
              <input id="pw" type="password" className="input" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            {needMfa && (
              <div className="field">
                <label htmlFor="mfa">{t('mfa.code')}</label>
                <input id="mfa" className="input num" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))} autoFocus />
                <span className="hint">{t('mfa.loginHint')}</span>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="field">
              <label htmlFor="ph">{t('auth.phone')}</label>
              <input id="ph" type="tel" className="input" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={codeSent} />
            </div>
            {codeSent && (
              <div className="field">
                <label htmlFor="otp">{t('auth.code')}</label>
                <input id="otp" className="input num" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus />
              </div>
            )}
          </>
        )}
        {error && <p role="alert" style={{ color: 'var(--danger)', margin: 0 }}>{error}</p>}
        <button className="btn primary lg block" disabled={busy}>
          {busy ? t('common.loading') : mode === 'phone' && !codeSent ? t('auth.sendCode') : mode === 'phone' ? t('auth.verify') : t('auth.login')}
        </button>
        <button type="button" className="btn ghost block" onClick={() => { setMode(mode === 'password' ? 'phone' : 'password'); setError(null); setCodeSent(false); }}>
          {mode === 'password' ? t('auth.usePhone') : t('auth.usePassword')}
        </button>
      </form>
      <div style={{ marginTop: 16 }}>
        <GoogleSignIn onSignedIn={done} />
        <GuestButton onSignedIn={done} />
      </div>
      <div className="grow" />
      <Link to={`/welcome${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} className="btn ghost block" style={{ marginTop: 24 }}>
        {t('auth.noAccount')}
      </Link>
    </div>
  );
}
